import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { BarChart3, CircleUserRound, LogIn, UserPlus, UserCircle, Settings, LogOut, Building2, Plus, Sun, Moon } from 'lucide-react'
import { useTheme } from '../theme/ThemeProvider.tsx'
import { Switch } from '../switch/Switch.tsx'
import styles from './UserMenu.module.css'

export interface UserMenuUser {
  name: string
  avatarUrl?: string
}

export interface UserMenuOrg {
  name: string
  slug: string
  logoUrl?: string
  brandColor?: string
  role: string
  isTeamCaptain?: boolean
}

/** One of the viewer's teams in the account menu. */
export interface UserMenuTeam {
  name: string
  /** Where the team's page lives. */
  href: string
  /** A short line under the name — the game, plus "Captain" when they captain it. */
  meta?: string
}

/** An extra link at the top of the signed-in menu, above Profile. */
export interface UserMenuItem {
  label: string
  to: string
  icon?: ReactNode
}

export interface UserMenuProps {
  /** Signed out when null: the menu offers Log In and Sign Up instead. */
  user?: UserMenuUser | null
  /** The viewer's organizations. Omit to leave the section out entirely. */
  organizations?: UserMenuOrg[]
  /** The teams the viewer plays on. The section shows only when there are some. */
  teams?: UserMenuTeam[]
  /** Links placed above Profile — e.g. "Dashboard" on a surface outside the product. */
  items?: UserMenuItem[]
  /** Rendered beside the name in the menu header (the Trust Score ring, typically). */
  menuExtra?: ReactNode
  createOrgUrl?: string
  profileUrl?: string
  /** The player's own stats page. Listed under Profile when given. */
  statsUrl?: string
  settingsUrl?: string
  /** What the settings link is called, e.g. "Your account". Default "Settings". */
  settingsLabel?: string
  onLogout?: () => void
  /**
   * The dark-mode switch inside the menu, shown at phone widths only. Hosts
   * that keep their own theme toggle in view at every width turn it off.
   */
  themeRow?: boolean
  className?: string
}

/**
 * The account menu: an avatar button that opens Profile, Settings, your
 * organizations and Log out. One component so the same account shows the same
 * menu on every surface — the app bar and the marketing nav both render it.
 */
export function UserMenu({
  user,
  organizations,
  teams,
  items,
  menuExtra,
  createOrgUrl = '/create-org',
  profileUrl = '/profile',
  statsUrl,
  settingsUrl = '/settings',
  settingsLabel = 'Settings',
  onLogout,
  themeRow = true,
  className,
}: UserMenuProps) {
  const { theme, toggleTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const close = () => setOpen(false)

  const themeSwitch = themeRow && (
    <div className={styles.themeRow}>
      <span className={styles.themeLabel}>
        {theme === 'dark' ? <Moon size={15} /> : <Sun size={15} />}
        Dark mode
      </span>
      <Switch size="sm" checked={theme === 'dark'} onChange={toggleTheme} aria-label="Toggle dark mode" />
    </div>
  )

  return (
    <div className={[styles.userMenu, className].filter(Boolean).join(' ')} ref={ref}>
      <button
        type="button"
        className={styles.avatarBtn}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="User menu"
      >
        {user && user.avatarUrl ? (
          <img src={user.avatarUrl} alt={user.name} className={styles.avatarImg} />
        ) : user ? (
          <span className={styles.avatarFallback}>
            {user.name.charAt(0).toUpperCase()}
          </span>
        ) : (
          <CircleUserRound size={20} className={styles.avatarIcon} />
        )}
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          {user ? (
            <>
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownName}>{user.name}</span>
                {menuExtra}
              </div>
              <div className={styles.dropdownDivider} role="separator" />
              {items?.map((item) => (
                <Link key={item.to} to={item.to} className={styles.dropdownItem} role="menuitem" onClick={close}>
                  {item.icon}
                  {item.label}
                </Link>
              ))}
              <Link to={profileUrl} className={styles.dropdownItem} role="menuitem" onClick={close}>
                <UserCircle size={15} />
                Profile
              </Link>
              {statsUrl && (
                <Link to={statsUrl} className={styles.dropdownItem} role="menuitem" onClick={close}>
                  <BarChart3 size={15} />
                  My stats
                </Link>
              )}
              <Link to={settingsUrl} className={styles.dropdownItem} role="menuitem" onClick={close}>
                <Settings size={15} />
                {settingsLabel}
              </Link>
              {teams && teams.length > 0 && (
                <>
                  <div className={styles.dropdownDivider} role="separator" />
                  <div className={styles.dropdownSectionLabel}>My Teams</div>
                  {teams.map((team) => (
                    <Link key={team.href} to={team.href} className={styles.dropdownTeam} role="menuitem" onClick={close}>
                      <span className={styles.dropdownTeamName}>{team.name}</span>
                      {team.meta && <span className={styles.dropdownTeamMeta}>{team.meta}</span>}
                    </Link>
                  ))}
                </>
              )}
              {organizations && organizations.length > 0 && (
                <>
                  <div className={styles.dropdownDivider} role="separator" />
                  <div className={styles.dropdownSectionLabel}>My Organizations</div>
                  {organizations.map((org) => (
                    <div key={org.slug} className={styles.dropdownOrgBlock}>
                      <div className={styles.dropdownOrgHeader}>
                        {org.logoUrl ? (
                          <img src={org.logoUrl} alt="" className={styles.dropdownOrgLogo} />
                        ) : (
                          <span
                            className={styles.dropdownOrgFallback}
                            style={org.brandColor ? { backgroundColor: org.brandColor } : undefined}
                          >
                            {org.name.charAt(0)}
                          </span>
                        )}
                        <span className={styles.dropdownOrgName}>{org.name}</span>
                      </div>
                      <div className={styles.dropdownOrgActions}>
                        <Link to={`/org/${org.slug}`} className={styles.dropdownOrgAction} onClick={close}>
                          View
                        </Link>
                        {(org.role === 'owner' || org.role === 'admin' || org.role === 'manager' || org.isTeamCaptain) && (
                          <Link to={`/org/${org.slug}/manage`} className={styles.dropdownOrgAction} onClick={close}>
                            Manage
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                  <Link to={createOrgUrl} className={styles.dropdownItem} role="menuitem" onClick={close}>
                    <Plus size={15} />
                    Create Organization
                  </Link>
                </>
              )}
              {organizations && organizations.length === 0 && (
                <>
                  <div className={styles.dropdownDivider} role="separator" />
                  <Link to={createOrgUrl} className={styles.dropdownItem} role="menuitem" onClick={close}>
                    <Building2 size={15} />
                    Create Organization
                  </Link>
                </>
              )}
              {themeSwitch}
              <div className={styles.dropdownDivider} role="separator" />
              <button
                type="button"
                className={styles.dropdownItem}
                role="menuitem"
                onClick={() => { close(); onLogout?.() }}
              >
                <LogOut size={15} />
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={styles.dropdownItem} role="menuitem" onClick={close}>
                <LogIn size={15} />
                Log In
              </Link>
              <Link to="/signup" className={styles.dropdownItem} role="menuitem" onClick={close}>
                <UserPlus size={15} />
                Sign Up
              </Link>
              {themeSwitch}
            </>
          )}
        </div>
      )}
    </div>
  )
}
